import React, { useId } from 'react';
import { cn } from '../../lib/utils';
import { icons } from './Icons';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      placeholder,
      className,
      id,
      'aria-describedby': describedByProp,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const errorId = `${selectId}-error`;
    const helperId = `${selectId}-helper`;
    const describedBy = [describedByProp, error && errorId, helperText && helperId]
      .filter(Boolean)
      .join(' ') || undefined;

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={selectId} className="mb-1.5 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {label}
            {props.required && (
              <>
                <span className="ml-1 text-emergency-600 dark:text-emergency-400" aria-hidden="true">*</span>
                <span className="sr-only"> erforderlich</span>
              </>
            )}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              'h-9 w-full appearance-none rounded-md border border-neutral-300 bg-white px-3 py-2 pr-9 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:focus:border-primary-400 dark:focus:ring-primary-400/20',
              error && 'border-emergency-500 focus:border-emergency-500 focus:ring-emergency-500/20 dark:border-emergency-500 dark:focus:border-emergency-400 dark:focus:ring-emergency-400/20',
              className
            )}
            {...props}
            aria-invalid={error ? true : undefined}
            aria-required={props.required || undefined}
            aria-describedby={describedBy}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden="true">
            <icons.ChevronDown className="h-4 w-4" />
          </div>
        </div>
        {error && (
          <p id={errorId} className="mt-1.5 text-xs text-emergency-700 dark:text-emergency-400" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="mt-1.5 text-xs leading-4 text-neutral-500 dark:text-neutral-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
